import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, CalendarDays, Clock, Phone } from "lucide-react";
import { Calendar } from "../ui/calendar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SlotPicker = ({ token, onBooked, onArrange, busy }) => {
  const [day, setDay] = useState(null);
  const [slots, setSlots] = useState([]);
  const [booked, setBooked] = useState([]);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [closures, setClosures] = useState([]);
  const [saving, setSaving] = useState(false);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  useEffect(() => { axios.get(`${API}/closures`).then(({ data }) => setClosures(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!day) return;
    setLoading(true); setTime("");
    axios.get(`${API}/quotes/${token}/availability`, { params: { date: format(day, "yyyy-MM-dd") } })
      .then(({ data }) => { setSlots(data.all || data.slots || []); setBooked(data.booked || []); })
      .catch(() => setSlots([])).finally(() => setLoading(false));
  }, [day, token]);
  const closed = (d) => { const s = format(d, "yyyy-MM-dd"); return closures.some((c) => s >= c.start_date && s <= c.end_date); };
  const book = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/quotes/${token}/book`, { preferred_date: format(day, "yyyy-MM-dd"), preferred_time: time });
      toast.success("Booked! Check your email for confirmation.");
      onBooked(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't book that time, please pick another."); }
    setSaving(false);
  };
  return (
    <div className="mt-6 rounded-xl border border-white/10 bg-[#0b0c0f] p-5" data-testid="quote-slot-picker">
      <div className="flex items-center gap-2 font-semibold text-white"><CalendarDays className="h-4 w-4 text-brand" />Pick a date & time for your visit</div>
      <div className="mt-3 flex justify-center">
        <Calendar mode="single" selected={day} onSelect={setDay} weekStartsOn={1} disabled={(d) => d < today || closed(d)}
          classNames={{ day_selected: "bg-brand text-white hover:bg-brand focus:bg-brand", day_today: "border border-brand/50" }} data-testid="quote-calendar" />
      </div>
      {day && (
        <div className="mt-3">
          {loading ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-brand" /> : slots.length === 0 ? <p className="text-center text-sm text-white/50">No times available on this day.</p> : (
            <div className="grid grid-cols-4 gap-2" data-testid="quote-slots">
              {slots.map((t) => {
                const taken = booked.includes(t);
                return <button key={t} disabled={taken} onClick={() => setTime(t)} className={`rounded-lg border py-2 text-sm font-semibold transition-colors ${time === t ? "border-brand bg-brand text-white" : taken ? "border-white/5 text-white/20 line-through" : "border-white/15 text-white/80 hover:border-brand"}`} data-testid={`quote-slot-${t}`}>{t}</button>;
              })}
            </div>
          )}
        </div>
      )}
      <button disabled={!day || !time || saving} onClick={book} className="btn-red mt-4 flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold disabled:opacity-50" data-testid="quote-book-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clock className="h-4 w-4" />}{day && time ? `Book ${format(day, "EEE d MMM")} at ${time}` : "Choose a date and time"}
      </button>
      <button disabled={busy} onClick={onArrange} className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 py-3 text-sm font-semibold text-white/70 hover:border-white/40 disabled:opacity-50" data-testid="quote-arrange-btn"><Phone className="h-4 w-4" />I'd rather EVT contacts me to arrange it</button>
    </div>
  );
};

export default SlotPicker;
